import { useMobileHeaderThemeVars } from "@circle-react-shared/uikit/Header/useMobileHeaderThemeVars";
import { MasqueradeBanner } from "@circle-react/components/MasqueradeBanner";
import { usePunditUserContext } from "@circle-react/contexts/punditUserContext";
import { MobileShell } from "@circleco/compass/components/MobileShell";
import { useLocation } from "@tanstack/react-router";
import type { ReactNode } from "react";

import { useMobileBottomBarOverride } from "../hooks/chrome/useMobileBottomBarOverride";
import { useMobileChromeVisibility } from "../hooks/chrome/useMobileChromeVisibility";
import { useMobileMainClassNameContext } from "../hooks/chrome/useMobileMainClassNameContext";
import type { MobileTopBarProps } from "../types";
import { getMobileMainWrapperClassName } from "./getMobileMainWrapperClassName";
import { MobileBottomNav } from "./MobileBottomNav";
import { MobileMemberChecklistFab } from "./MobileMemberChecklistFab";
import { MobileSectionTabs } from "./MobileSectionTabs";
import { MobileTopBarArea } from "./MobileTopBarArea";

/**
 * Inner shell content that lives inside MobileSectionTabsProvider so it can
 * read chrome-visibility context set by pages (all ref-counted, see
 * useMobileChromeVisibility):
 * - `bottomBarOverride` (see useMobileBottomBar) swaps the bottom nav's
 *   content and keeps the bottom bar visible even when `isBottomNavHidden`.
 * - `isChromeHidden` suppresses everything - TopBar, section tabs, back
 *   bar, and bottom nav - for pages with their own full chrome (e.g.
 *   settings pages).
 * - `isTopNavHidden` / `isBottomNavHidden` each suppress just their own
 *   side, leaving the other in place (e.g. Admin Settings hides only the
 *   bottom nav).
 *
 * `isFullBleedContent` / `isFixedHeightContent` pass straight through as
 * `MobileShell.Content`'s `fullBleed` / `fixedHeight` props, letting pages
 * opt out of default padding or manage their own bounded scroll region.
 *
 * `mainClassName` (see `useMobileMainClassName`) is passed straight through
 * as `MobileShell.Content`'s `className`, applied directly to `<main>`.
 */
interface MobileShellInnerProps extends MobileTopBarProps {
  children: ReactNode;
  isNavOpen: boolean;
  setIsNavOpen: (open: boolean) => void;
}

export const MobileShellInner = ({
  children,
  isNavOpen,
  setIsNavOpen,
  ...topBarProps
}: MobileShellInnerProps) => {
  const pathname = useLocation({ select: location => location.pathname });
  const { currentUser, currentCommunityMember } = usePunditUserContext();
  const {
    isChromeHidden: isChromeHiddenFromContext,
    isBottomNavHidden,
    isTopNavHidden,
    isFullBleedContent,
    isFixedHeightContent,
    isTopBarUnbranded,
  } = useMobileChromeVisibility();
  const { mainClassName } = useMobileMainClassNameContext();
  const { bottomBarOverride } = useMobileBottomBarOverride();
  const headerThemeVars = useMobileHeaderThemeVars();
  // Live stream/room pages handle their own full-screen layout so no shell
  // chrome should overlay them. The TanStack `_minimal` layout already skips
  // the shell entirely, but on the legacy react-router path `/live/:roomSlug`
  // falls through to StandardLayoutV2 → MobileLayoutShell on the legacy path.
  const isLivePage = pathname.startsWith("/live/");
  const isChromeHidden = isChromeHiddenFromContext || isLivePage;
  const isMember = Boolean(currentUser && currentCommunityMember);
  // Combine the all-chrome flag with each side's own narrower flag, since
  // either can independently suppress that side.
  const isTopHidden = isChromeHidden || isTopNavHidden;
  // A bottom bar override outranks the narrower hide flag (a page that
  // registers its own bar wants it shown even under a layout, like
  // settings, that hides the default nav) but not `isChromeHidden`.
  const isBottomHidden =
    isChromeHidden || (isBottomNavHidden && !bottomBarOverride);
  const mainWrapperClassName = getMobileMainWrapperClassName({
    isTopHidden,
    isBottomHidden,
  });

  const content = (
    <>
      {!isTopHidden && (
        <MobileShell.Header
          themeVars={isTopBarUnbranded ? undefined : headerThemeVars}
        >
          <MasqueradeBanner isCompact />
          <MobileTopBarArea
            isNavOpen={isNavOpen}
            setIsNavOpen={setIsNavOpen}
            {...topBarProps}
          />
          <MobileSectionTabs />
        </MobileShell.Header>
      )}

      <MobileShell.Content
        id={isFixedHeightContent ? undefined : "panel"}
        scrollRestorationId={isFixedHeightContent ? undefined : "panel"}
        className={mainClassName ?? undefined}
        fullBleed={isFullBleedContent}
        fixedHeight={isFixedHeightContent}
      >
        {children}
      </MobileShell.Content>

      {!isBottomHidden && isMember && (
        <MobileShell.Footer>
          {!bottomBarOverride && (
            <MobileMemberChecklistFab currentPath={pathname} />
          )}
          <MobileShell.BottomBar>
            {bottomBarOverride ?? <MobileBottomNav currentPath={pathname} />}
          </MobileShell.BottomBar>
        </MobileShell.Footer>
      )}
    </>
  );

  return <div className={mainWrapperClassName ?? undefined}>{content}</div>;
};
